import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import blogImg from "../assests/images/blog-bg.jpg";
import axios from "axios";
import { Helmet } from "react-helmet-async";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const res = await axios.get("https://dubaicheaphotels.com/api/blogs");

        const data = res.data;

        setBlogs(data);

        const uniqueCategories = [
          ...new Map(
            data
              .filter((b) => b.category)
              .map((b) => [b.category.id, b.category])
          ).values(),
        ];

        setCategories(uniqueCategories);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      .toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory = selectedCategory
      ? blog.category?.id === Number(selectedCategory)
      : true;

    return matchesSearch && matchesCategory;
  });

  return (

    <>
      <Helmet>
        <title>
          Travel Blogs & Hotel Guides | Dubai Cheap Hotels
        </title>

        <meta
          name="description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Dubai Cheap Hotels."
        />

        <link
          rel="canonical"
          href="https://dubaicheaphotels.com/blogs"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Travel Blogs & Hotel Guides | Dubai Cheap Hotels"
        />

        <meta
          property="og:description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Dubai Cheap Hotels."
        />

        <meta
          property="og:url"
          content="https://dubaicheaphotels.com/blogs"
        />

        <meta
          property="og:type"
          content="website"
        />

        {/* Twitter */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Travel Blogs & Hotel Guides | Dubai Cheap Hotels"
        />

        <meta
          name="twitter:description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Dubai Cheap Hotels."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>


      <div className="bg-gray-50">
        <Hero
          title="Travel Blog"
          subtitle="Explore travel guides, tips and inspiration"
          backgroundImage={blogImg}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 relative">
          <div className="absolute -top-10 -left-10 w-72 h-72 bg-orange-300 opacity-20 rounded-full blur-3xl"></div>

          {/* SEARCH + FILTER */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 sm:mb-10">
            <input
              type="text"
              placeholder="Search blogs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:flex-1 px-4 py-3 rounded-xl border focus:ring-2 focus:ring-[#FF6B35] outline-none text-sm sm:text-base"
            />

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full sm:w-auto px-4 py-3 rounded-xl border focus:ring-2 focus:ring-[#FF6B35] text-sm sm:text-base"
            >
              <option value="">All Categories</option>

              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* LOADING */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="bg-white p-4 rounded-xl shadow animate-pulse"
                >
                  <div className="h-40 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 mb-2 rounded"></div>
                  <div className="h-4 bg-gray-200 w-2/3 rounded"></div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length > 0 ? (
            <>
              {/* FEATURED BLOG */}
              <div className="mb-10 sm:mb-12">
                <Link to={`/blogs/${filteredBlogs[0].slug}`}>
                  <div className="relative h-[220px] sm:h-[300px] lg:h-[400px] rounded-2xl sm:rounded-3xl overflow-hidden group shadow-xl">
                    <img
                      src={filteredBlogs[0].thumbnail_image}
                      alt={filteredBlogs[0].title}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>

                    <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white max-w-[90%] sm:max-w-xl">
                      <span className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] px-3 py-1 text-[10px] sm:text-xs rounded-full text-white">
                        {filteredBlogs[0].category?.name}
                      </span>

                      <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mt-2 sm:mt-3">
                        {filteredBlogs[0].title}
                      </h1>

                      <p className="text-xs sm:text-sm mt-1 sm:mt-2 opacity-90 line-clamp-2">
                        {filteredBlogs[0].excerpt}
                      </p>
                      <span className="text-[10px] sm:text-xs text-gray-400">
                        {new Date(filteredBlogs[0].created_at).toDateString()}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>

              {/* BLOG GRID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {filteredBlogs.slice(1).map((blog) => (
                  <div
                    key={blog.id}
                    className="bg-white rounded-2xl shadow-md hover:shadow-[0_20px_40px_rgba(255,107,53,0.15)] transition overflow-hidden group border border-gray-100"
                  >
                    <Link to={`/blogs/${blog.slug}`}>
                      <div className="relative h-40 sm:h-48 overflow-hidden">
                        <img
                          src={blog.thumbnail_image}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                        />

                        <span className="absolute top-3 left-3 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white text-[10px] sm:text-xs px-3 py-1 rounded-full">
                          {blog.category?.name}
                        </span>
                      </div>
                    </Link>

                    <div className="p-4 sm:p-5">
                      <Link to={`/blogs/${blog.slug}`}>
                        <h3 className="font-semibold text-base sm:text-lg text-gray-800 line-clamp-2 hover:text-[#FF6B35]">
                          {blog.title}
                        </h3>
                      </Link>

                      <p className="text-gray-500 text-sm mt-2 line-clamp-3">
                        {blog.excerpt}
                      </p>

                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-4">
                        <img
                          src="https://i.pravatar.cc/49"
                          className="w-6 h-6 rounded-full"
                          alt="author"
                        />
                        <span>Admin</span>
                        <span>• 5 min read</span>
                      </div>

                      <div className="mt-4 flex justify-between items-center">
                        <span className="text-[10px] sm:text-xs text-gray-400">
                          {new Date(blog.created_at).toDateString()}
                        </span>

                        <Link
                          to={`/blogs/${blog.slug}`}
                          className="text-xs sm:text-sm font-medium bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] bg-clip-text text-transparent hover:underline"
                        >
                          Read More →
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500">No blogs found</p>
          )}
        </div>
      </div>
    </>
  );
}